//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';

import FacebookUserPermission from '@models/FacebookUserPermission';
import { makeParams } from '@utils/makeParams';

import { crl8 } from 'app/curalate.globals';
import { OAuthCsrfSalt, deriveOAuthCsrfToken } from 'app/oauth/oauthCsrf';

// Assume mining takes no longer than 10 minutes
const FACEBOOK_MINING_TIME_MILLI = 10 * 60 * 1000;

export const isMiningInProcess = (pageTokenDateAdded: string) => {
  const pageTokenDateAddedMilli = new Date(pageTokenDateAdded).valueOf();
  return pageTokenDateAddedMilli > Date.now() - FACEBOOK_MINING_TIME_MILLI;
};

const DEFAULT_FB_REQUEST_PERMISSIONS = [
  // Insights
  FacebookUserPermission.ReadInsights,

  // Manage pages
  FacebookUserPermission.PagesManageAds,
  FacebookUserPermission.PagesManageMetadata,
  FacebookUserPermission.PagesReadEngagement,
  FacebookUserPermission.PagesReadUserContent,

  // Publish to pages
  FacebookUserPermission.PagesManagePosts,
  FacebookUserPermission.PagesManageEngagement,

  // Misc
  FacebookUserPermission.BusinessManagement,
  FacebookUserPermission.AdsManagement,
  FacebookUserPermission.CatalogManagement,
  FacebookUserPermission.AdsRead,

  // Instagram
  FacebookUserPermission.InstagramBasic,
  FacebookUserPermission.InstagramManageComments,
  FacebookUserPermission.InstagramManageInsights,
  FacebookUserPermission.InstagramContentPublish,
  FacebookUserPermission.InstagramShoppingTagProducts,
  FacebookUserPermission.InstagramManageMessages,
];
export const DEFAULT_FB_OAUTH_REDIRECT_PATH = '/account-settings/social-media';
/**
 * Whitelist of allowed redirect paths.
 * Since attackers could attempt to CSRF via OAuth URLs, we need to strictly validate these.
 */
export const VALID_FB_OAUTH_REDIRECT_PATHS = PojoSet.from([
  '/settings/account',
  '/facebook/summary',
  DEFAULT_FB_OAUTH_REDIRECT_PATH,
]);
export type ValidFBOAuthRedirectPath =
  keyof typeof VALID_FB_OAUTH_REDIRECT_PATHS;

export const generateFacebookOAuthUrl = async (
  isRerequest = false,
  redirectPath: ValidFBOAuthRedirectPath = DEFAULT_FB_OAUTH_REDIRECT_PATH,
) => {
  // OAuth is an HTTP-GET-based process, so it is vulnerable to CSRF.
  // We should generate & validate our own CSRF token for this process.

  const csrfToken = await deriveOAuthCsrfToken(OAuthCsrfSalt.Facebook);
  const state = JSON.stringify({
    csrfToken,
    redirectPath,
    clientId: crl8.session?.client.id,
  });
  const params = makeParams({
    client_id: CURALATE_ENV.FACEBOOK_APP_ID,
    state,
    scope: DEFAULT_FB_REQUEST_PERMISSIONS.join(','),
    redirect_uri: `${location.origin}/facebook/oauth`,
    response_type: 'code,granted_scopes',
    ...(isRerequest ? { auth_type: 'rerequest' } : {}),
  });

  return `https://www.facebook.com/dialog/oauth?${params}`;
};
